import React, { useEffect, useState } from "react";
import { FileText, Loader2 } from "lucide-react";

import apiService from "@/lib/api";

import { fmtBytes } from "./threadFormat";

const ATTACHMENTS_BASE = "/api/v2/outreach/communications/attachments";

// Fetches an attachment's bytes once (auth-gated endpoint, so a bare <img src>
// can't be used — same reasoning as the resume-preview flows elsewhere in the
// app) and renders an inline thumbnail for images or a download chip for
// anything else. One fetch per mount; bubbles persist across the thread poll
// (React reconciles by the same item key), so this does not re-fetch on every
// refresh.
const AttachmentPreview = ({ attachment, outbound }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.getSecure(`${ATTACHMENTS_BASE}/${attachment.id}`, {
          responseType: "blob",
        });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setBlobUrl(objectUrl);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.id]);

  if (failed) {
    return <p className="mt-1.5 text-[11px] italic opacity-70">Attachment unavailable</p>;
  }

  const chipCls = outbound
    ? "bg-text-base/15 text-text-base hover:bg-text-base/20"
    : "bg-background-secondary text-text-primary border border-border-primary hover:bg-background-primary";

  if (attachment.is_image) {
    return blobUrl ? (
      <a href={blobUrl} target="_blank" rel="noreferrer" className="mt-1.5 block">
        <img src={blobUrl} alt={attachment.filename} className="max-h-64 max-w-full rounded-lg object-cover" />
      </a>
    ) : (
      <div className={`mt-1.5 flex h-28 w-40 items-center justify-center rounded-lg ${outbound ? "bg-text-base/10" : "bg-background-secondary"}`}>
        <Loader2 size={16} className="animate-spin opacity-60" aria-hidden="true" />
      </div>
    );
  }

  return (
    <a
      href={blobUrl || undefined}
      download={attachment.filename}
      className={`mt-1.5 flex items-center gap-2 rounded-lg px-2.5 py-2 text-xs transition-colors ${chipCls}`}
    >
      {blobUrl ? <FileText size={14} className="shrink-0" aria-hidden="true" /> : <Loader2 size={14} className="shrink-0 animate-spin" aria-hidden="true" />}
      <span className="min-w-0 flex-1 truncate">{attachment.filename}</span>
      <span className="shrink-0 opacity-70">{fmtBytes(attachment.file_size)}</span>
    </a>
  );
};

export default AttachmentPreview;
